import { useEffect, useCallback, useRef } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import {
  CartItem,
  CartItemDesign,
  CartItemLogo,
} from "../presentation/screens/order-create/models/CartItem";
import { BaseLogo } from "../core/models/entities/BaseLogo";
import { Design } from "../core/models/entities/Design";
import { useProfileContext } from "../contexts/ProfileContext";
import { useApiClient } from "../data/clients/ApiClient";
import { actAsCacheKey } from "../contexts/actAsStore";
import { mapToLogo } from "../data/models/mappers/mapLogo";
import { mapToDesign } from "../data/models/mappers/mapDesign";
import {
  cartDesignPendingChanged,
  cartLogoPendingChanged,
} from "../utils/functions/productPending";
import { patchLogoRowsFromDetail } from "../data/repositories/logoDetailCache";
import {
  patchDesignPricesInLists,
  patchLogoPricesInLists,
} from "../data/repositories/cartPriceListSync";
import { hasAdjustedPrices } from "../data/repositories/designCachePatch";
import { enviroment } from "../enviroment";
import { priceFieldsDiffer } from "../utils/functions/cartPriceCheck";
import { collectCartLogoIds } from "../presentation/screens/order-confirmation/storageVerify";
import { landCartPriceCheck } from "./useCartPriceCheck";

// Cart price check (flag cartPriceCheckEnabled): a design line's total
// prices are compared like a logo line's price rows, so the ONE GetDesign
// the check asks for on "updated" lands the fresh total in the line. A
// fresh total without a single ADJUSTED row - totalPrices is [NotMapped]
// in the API and only AdjustPrice fills it, so an answer where it did not
// run (a transient price failure, a pending product) carries it empty or
// as persisted-only rows - carries no information: the line keeps the
// price it has, exactly as the design cache patch keeps its cached
// container (designCachePatch, PRICES); never "stale", never the note's
// fallback. Always false with the flag off (the historical rule: storage /
// pending only).
function designTotalPricesChanged(
  cartDesign: CartItemDesign,
  freshDesign: Design
): boolean {
  if (!enviroment.cartPriceCheckEnabled) return false;
  if (!hasAdjustedPrices(freshDesign.product?.totalPrices)) return false;
  return priceFieldsDiffer(
    cartDesign.design.product?.totalPrices,
    freshDesign.product?.totalPrices
  );
}

interface UseCartSyncProps {
  items: CartItem[];
  updateItem: (item: CartItem) => Promise<CartItem>;
}

interface UseCartSyncReturn {
  isRefreshing: boolean;
  hasStaleItems: boolean;
  refreshCart: () => void;
}

export function useCartSync({
  items,
  updateItem,
}: UseCartSyncProps): UseCartSyncReturn {
  const apiClient = useApiClient();
  const { profile } = useProfileContext();
  const queryClient = useQueryClient();

  // Extract logo items from cart
  const logoItems = items.filter(
    (item): item is CartItemLogo => item.type === "Logo"
  );

  // Extract design items from cart
  const designItems = items.filter(
    (item): item is CartItemDesign => item.type === "Design"
  );

  // Create queries for all logos in the cart.
  // Key shape MUST match LogoRepository.useLogo (["logos", actAs, id]) so
  // cart sync and the detail popup share one cache entry; getLogo varies with
  // the act-as scope since #603 (prices in the acted-as vendor's currency),
  // hence the key segment. Same shared helper as the repository's own keys.
  const logoActAsKey = actAsCacheKey();
  const logoQueries = useQueries({
    queries: logoItems.map((logoItem) => ({
      queryKey: ["logos", logoActAsKey, logoItem.logoId],
      queryFn: () =>
        apiClient
          .getLogo(
            profile?.userOrganizationInformations.organizationId || 0,
            logoItem.logoId
          )
          .then(mapToLogo),
      enabled:
        !!profile?.userOrganizationInformations.organizationId &&
        logoItem.logoId > 0,
      staleTime: 1 * 30 * 1000,
    })),
  });

  // Create queries for all designs in the cart.
  // Key shape MUST match DesignRepository.useDesign (["designs", actAs, id])
  // so cart sync and the detail modal share one cache entry; getDesign now
  // varies with the act-as scope (actAsOrgId param), hence the key segment.
  // Same shared helper as the logo key below (actAsCacheKey == getActAsOrgId() ?? 0).
  const designActAsKey = actAsCacheKey();
  const designQueries = useQueries({
    queries: designItems.map((designItem) => ({
      queryKey: ["designs", designActAsKey, designItem.design.id],
      queryFn: () =>
        apiClient
          .getDesign(
            designItem.design.customer?.id ||
              profile?.userOrganizationInformations.organizationId ||
              0,
            designItem.design.id
          )
          .then(mapToDesign),
      enabled:
        (!!designItem.design.customer?.id ||
          !!profile?.userOrganizationInformations.organizationId) &&
        designItem.design.id > 0,
      staleTime: 1 * 30 * 1000,
    })),
  });

  // "Prices on their way" / "Preparing logo…" (flags productPending /
  // cloneInstant): a logo line's fresh detail is copied into the cached
  // LIST rows of the same logo (pending flags + product only), so the logo
  // list the user returns to has dropped the badge already - 0 calls; the
  // LIST entries are written, never the detail keys these queries observe,
  // so this can never re-trigger the sync. Both flags off = no-op.
  //
  // Keyed on WHEN each line's detail landed (id:dataUpdatedAt), not on
  // logoQueries (a fresh array every render of the cart): the cache walk
  // runs once per landed GetLogo, not once per keystroke in a quantity
  // field. The queries themselves are read through a ref at that moment.
  const logoQueriesRef = useRef(logoQueries);
  logoQueriesRef.current = logoQueries;
  const landedSignature = logoQueries
    .map((query) =>
      query.isSuccess && query.data
        ? `${(query.data as BaseLogo).id}:${query.dataUpdatedAt}`
        : ""
    )
    .join("|");
  // The price walks below run ONCE per landed entry (logo id -> the
  // dataUpdatedAt it was last walked with): a refresh of N lines lands N
  // times and each landing re-runs the effect, so walking every landed
  // query each time would be N walks per landing (N^2 per refresh) for the
  // same result - every walk but the first is a diff that writes nothing.
  // An entry whose stamp is unchanged since its last walk is skipped; a line
  // that left the cart leaves the map. Sub-flag off = the maps stay empty.
  const logoPriceStampsRef = useRef<Map<number, number>>(new Map());
  useEffect(() => {
    if (landedSignature.replace(/\|/g, "") === "") return;
    // The price follows to the list (sub-flag cartPriceCheckListSyncEnabled):
    // FIRST every newly landed logo's price rows are copied into the cached
    // list rows of the same id (0 calls, freshness stamps kept), THEN the
    // pending patches below. The order matters: patchLogoRowsFromDetail
    // writes with a plain setQueryData that stamps the page `now`, so a page
    // stamped by logo A's pending patch would be "newer" than logo B's
    // landing in the same effect and B's price patch would skip it; the
    // price patch keeps the stamp and never affects the pending patch's own
    // newer-guard. Under the sub-flag the pending patch keeps the page's
    // stamp as well (preserveFreshness below), so the same holds ACROSS
    // effect runs: B's GetLogo landing in the window between A's commit and
    // A's effect can no longer find the page stamped after its own landing
    // (skeptic round 3) - no local patch is ever newer than a server
    // landing. Sub-flag off = the historical plain write.
    if (enviroment.cartPriceCheckListSyncEnabled) {
      const walked = logoPriceStampsRef.current;
      const next = new Map<number, number>();
      logoQueriesRef.current.forEach((query) => {
        if (!query.isSuccess || !query.data) return;
        const logo = query.data as BaseLogo;
        next.set(logo.id, query.dataUpdatedAt);
        if (walked.get(logo.id) === query.dataUpdatedAt) return;
        patchLogoPricesInLists(queryClient, logo, query.dataUpdatedAt);
      });
      logoPriceStampsRef.current = next;
    }
    // Flag REACT_APP_LOGO_PRICE_DETAIL_PATCH_ENABLED (B) keeps the page's
    // stamp as well: its patch runs in prod where the sub-flag is off, and
    // two lines landed in the same effect pass (a bulk add from the list)
    // would otherwise heal only the FIRST row - the plain write stamps the
    // page `now`, and the second line's detail (landed a moment earlier) is
    // then "older than the list" and skipped by the newer-list guard. Same
    // write, same guarantee as the sub-flag; B off = unchanged.
    const pendingOptions =
      enviroment.cartPriceCheckListSyncEnabled ||
      enviroment.logoPriceDetailPatchEnabled
        ? { preserveFreshness: true }
        : undefined;
    logoQueriesRef.current.forEach((query) => {
      if (query.isSuccess && query.data) {
        patchLogoRowsFromDetail(queryClient, query.data as BaseLogo, pendingOptions);
      }
    });
  }, [landedSignature, queryClient]);

  // The price follows to the list, design lines (sub-flag
  // cartPriceCheckListSyncEnabled): a landed GetDesign is the ONE re-read the
  // check pays for, and it carries the design's fresh totalPrices/products
  // and its placed logos' rows AS PRICED IN THE DESIGN. They are copied into
  // the design's own row in every cached design list page - 0 calls. They
  // are NOT copied into the logo lists, the customer logo tab or a placed
  // logo's own detail entry (skeptic round 2, verified in the API 12/9):
  // GetDesign prices a placed logo with the design's vendor+customer NAV
  // numbers, the vendor's real commission, the vendor's currency and the
  // DESIGN customer's commission; GetLogo/GetAllLogos with the caller's
  // vendor NAV number only, a dummy commission, the caller's currency and
  // the LOGO's customer's commission - so the rows differ for vendors too,
  // and writing them would put another context's price on the logo row and
  // in the popup and, for a logo that is also a logo line, make that line
  // flap between two contexts on every refresh (two updateItem per refresh,
  // no "Price updated" note). A placed logo's own rows refresh through its
  // own GetLogo: the logo effect above when it is a logo line, else the ONE
  // GetLogo the check fires on "updated" for a logo placed in design lines
  // only (useCartPriceCheck.landPlacedLogoInLists, skeptic round 3 - it
  // lands in the logo's detail entry and patches the logo lists itself; no
  // observer here, so it never enters this hook). Same idiom
  // as the logo effect: keyed on WHEN each design landed (id:dataUpdatedAt),
  // one cache walk per newly landed design (stamp map, see above), never per
  // render; queries read through a ref at that moment. Sub-flag off = the
  // effect returns at once (byte-identical cache).
  const designQueriesRef = useRef(designQueries);
  designQueriesRef.current = designQueries;
  const designPriceStampsRef = useRef<Map<number, number>>(new Map());
  const designLandedSignature = designQueries
    .map((query) =>
      query.isSuccess && query.data
        ? `${(query.data as Design).id}:${query.dataUpdatedAt}`
        : ""
    )
    .join("|");
  useEffect(() => {
    if (!enviroment.cartPriceCheckListSyncEnabled) return;
    if (designLandedSignature.replace(/\|/g, "") === "") return;
    const walked = designPriceStampsRef.current;
    const next = new Map<number, number>();
    designQueriesRef.current.forEach((query) => {
      if (!query.isSuccess || !query.data) return;
      const design = query.data as Design;
      next.set(design.id, query.dataUpdatedAt);
      if (walked.get(design.id) === query.dataUpdatedAt) return;
      patchDesignPricesInLists(queryClient, design, query.dataUpdatedAt);
    });
    designPriceStampsRef.current = next;
  }, [designLandedSignature, queryClient]);

  // Check if any queries are loading
  const isRefreshing =
    logoQueries.some((query) => query.isLoading || query.isFetching) ||
    designQueries.some((query) => query.isLoading || query.isFetching);

  // Function to check if a logo has stale data
  const isLogoStale = useCallback(
    (cartLogo: CartItemLogo, freshLogo: BaseLogo): boolean => {
      // "Prices on their way" (flag productPendingEnabled): a pending state
      // that changed is a reason to sync on its own - the worker may land
      // the very same prices, and the line must still drop its badge/lock.
      // Always false with the flag off.
      if (cartLogoPendingChanged(cartLogo.baseLogo, freshLogo)) return true;

      // Compare prices directly using product.prices (row count, then the
      // fields row by row - the rule lives in priceFieldsDiffer, unchanged).
      return priceFieldsDiffer(
        cartLogo.baseLogo.product?.prices,
        freshLogo.product?.prices
      );
    },
    []
  );

  // Function to check if a design has stale data
  const isDesignStale = useCallback(
    (cartDesign: CartItemDesign, freshDesign: Design): boolean => {
      // "Prices on their way" (flag productPendingEnabled): see isLogoStale -
      // covers the design's flag, its placed logos and the instance rows'
      // baseLogo copies. Always false with the flag off.
      if (cartDesignPendingChanged(cartDesign, freshDesign)) return true;

      // Cart price check (flag cartPriceCheckEnabled): the design's total
      // prices moved - the line must take the fresh total. Always false
      // with the flag off.
      if (designTotalPricesChanged(cartDesign, freshDesign)) return true;

      // Check if design logos have changed in storage or prices
      // This is simplified; ideally we check everything, but focusing on inStorage
      const cartLogos = cartDesign.design.designLogos || [];
      // Compare against the fresh logos minus any newly-deleted ones, since the
      // cart intentionally filters out deleted logos at entry.
      const freshLogos = (freshDesign.designLogos || []).filter(
        (dl) => !dl.logo.isDeleted
      );

      if (cartLogos.length !== freshLogos.length) return true;

      // Check if any logo has changed storage
      return cartLogos.some((cartLogo) => {
        const freshLogo = freshLogos.find((f) => f.logoId === cartLogo.logoId);
        if (!freshLogo) return true;

        const cartStorage = cartLogo.logo.inStorage ?? 0;
        const freshStorage = freshLogo.logo.inStorage ?? 0;

        if (cartStorage !== freshStorage) return true;

        // Also check prices of logos if needed, similar to isLogoStale
        // But inStorage is the main concern here
        return false;
      });
    },
    []
  );

  // Cart price check (flag cartPriceCheckEnabled): EVERY landed detail is
  // handed to the check - stale or not - so a logo the API reported
  // "updated" has its mark consumed by the very re-read the check asked
  // for, and the "Price updated" note is decided against the rows the user
  // saw at add time (landCartPriceCheck). Idempotent after the first
  // landing (the mark is gone), so the per-render re-run costs a few map
  // reads. Skipped entirely with the flag off.
  const landCartPriceChecks = useCallback(() => {
    if (!enviroment.cartPriceCheckEnabled) return;
    logoQueries.forEach((query, index) => {
      const cartItem = logoItems[index];
      const freshLogo = query.data;
      if (!query.isSuccess || !freshLogo) return;
      landCartPriceCheck(
        cartItem.logoId,
        freshLogo.product?.prices,
        priceFieldsDiffer(
          cartItem.baseLogo.product?.prices,
          freshLogo.product?.prices
        )
      );
    });
    designQueries.forEach((query, index) => {
      const cartItem = designItems[index];
      const freshDesign = query.data;
      if (!query.isSuccess || !freshDesign) return;
      // The fresh design carries each placed logo's own price rows; the
      // design total's diff is the fallback for a logo it does not list.
      const totalDiffers = designTotalPricesChanged(cartItem, freshDesign);
      collectCartLogoIds([cartItem]).forEach((logoId) => {
        const placed = (freshDesign.designLogos ?? []).find(
          (dl) => dl.logoId === logoId
        );
        landCartPriceCheck(logoId, placed?.logo?.product?.prices, totalDiffers);
      });
    });
  }, [logoQueries, designQueries, logoItems, designItems]);

  // Function to update stale cart items
  const syncStaleItems = useCallback(async () => {
    const updates: Promise<CartItem>[] = [];

    landCartPriceChecks();

    logoQueries.forEach((query, index) => {
      const cartItem = logoItems[index];
      const freshLogo = query.data;

      if (query.isSuccess && freshLogo && isLogoStale(cartItem, freshLogo)) {
        // Create updated cart item with fresh logo data
        const updatedCartItem: CartItemLogo = {
          ...cartItem,
          baseLogo: freshLogo,
        };

        updates.push(updateItem(updatedCartItem));
      }
    });

    designQueries.forEach((query, index) => {
      const cartItem = designItems[index];
      const freshDesign = query.data;

      if (
        query.isSuccess &&
        freshDesign &&
        isDesignStale(cartItem, freshDesign)
      ) {
        // Re-apply the same filter the cart uses at entry: deleted logos must
        // not appear in design.designLogos, but are tracked separately so the
        // UI can show a warning and the order omits them.
        const freshDeletedDesignLogos = (freshDesign.designLogos ?? []).filter(
          (dl) => dl.logo.isDeleted
        );
        const filteredFreshDesign: Design = {
          ...freshDesign,
          designLogos: (freshDesign.designLogos ?? []).filter(
            (dl) => !dl.logo.isDeleted
          ),
        };

        const updatedCartItem: CartItemDesign = {
          ...cartItem,
          design: filteredFreshDesign,
          excludedDeletedDesignLogos:
            freshDeletedDesignLogos.length > 0
              ? freshDeletedDesignLogos
              : undefined,
          // Update instances' logos' baseLogos with fresh data, but skip any
          // instance logo that has become deleted.
          instances: cartItem.instances.map((inst) => ({
            ...inst,
            logos: inst.logos.map((l) => {
              const freshLogoWrapper = filteredFreshDesign.designLogos.find(
                (dl) => dl.logoId === l.logoId
              );
              if (freshLogoWrapper) {
                return { ...l, baseLogo: freshLogoWrapper.logo };
              }
              return l;
            }),
          })),
        };

        updates.push(updateItem(updatedCartItem));
      }
    });

    if (updates.length > 0) {
      try {
        await Promise.all(updates);
      } catch (error) {
        console.error("Failed to update stale cart items:", error);
      }
    }
  }, [
    logoQueries,
    designQueries,
    logoItems,
    designItems,
    isLogoStale,
    isDesignStale,
    updateItem,
    landCartPriceChecks,
  ]);

  // Check if there are any stale items
  const hasStaleItems =
    logoQueries.some((query, index) => {
      const cartItem = logoItems[index];
      const freshLogo = query.data;
      return query.isSuccess && freshLogo && isLogoStale(cartItem, freshLogo);
    }) ||
    designQueries.some((query, index) => {
      const cartItem = designItems[index];
      const freshDesign = query.data;
      return (
        query.isSuccess && freshDesign && isDesignStale(cartItem, freshDesign)
      );
    });

  // Auto-sync when fresh data is available
  useEffect(() => {
    if (
      !isRefreshing &&
      (logoQueries.length > 0 || designQueries.length > 0) &&
      logoQueries.every((q) => q.isSuccess || q.isError) &&
      designQueries.every((q) => q.isSuccess || q.isError)
    ) {
      syncStaleItems();
    }
  }, [isRefreshing, logoQueries, designQueries, syncStaleItems]);

  // Manual refresh function
  const refreshCart = useCallback(() => {
    logoQueries.forEach((query) => {
      if (query.refetch) {
        query.refetch();
      }
    });
    designQueries.forEach((query) => {
      if (query.refetch) {
        query.refetch();
      }
    });
  }, [logoQueries, designQueries]);

  return {
    isRefreshing,
    hasStaleItems,
    refreshCart,
  };
}
